import { inject, Injectable } from '@angular/core';
import { updatePreset, updatePrimaryPalette, updateSurfacePalette } from '@openng/optimus-ui-themes';
import Aura from '@openng/optimus-ui-themes/aura';
import { KeyOfType, SurfacesType } from './types/theme.types';
import { defaultMenuThemes } from '../../content/service/types/user.types';
import { AppConfig } from './layout.service';
import { changeLanguage } from '../store/layout.actions';
import { LanguageEnum } from '../../shared/models/language.enum';
import { Store } from '@ngrx/store';

@Injectable({
    providedIn: 'root',
})
export class ThemeService {
    private readonly store = inject(Store);

    public setTheme(theme: AppConfig) {
        this.store.dispatch(changeLanguage({ language: theme.language || LanguageEnum.CZECH }));
        this.setComponentTheme(theme.componentTheme);
        this.setMenuTheme(theme.menuTheme);
        this.setSurface(theme.surface);
        this.setScale(theme.scale);
        this.toggleDarkMode(theme.colorScheme);
    }

    private setComponentTheme(componentColorTheme: string) {
        const presetPalette = Aura.primitive;
        const colorPalette = presetPalette?.[componentColorTheme as KeyOfType<typeof presetPalette>] as SurfacesType['palette'];

        updatePrimaryPalette(colorPalette ?? {});
    }

    private setSurface(surface: string) {
        updateSurfacePalette({
            50: `{${surface}.50}`,
            100: `{${surface}.100}`,
            200: `{${surface}.200}`,
            300: `{${surface}.300}`,
            400: `{${surface}.400}`,
            500: `{${surface}.500}`,
            600: `{${surface}.600}`,
            700: `{${surface}.700}`,
            800: `{${surface}.800}`,
            900: `{${surface}.900}`,
            950: `{${surface}.950}`,
        });
    }

    private setMenuTheme(menuTheme: string) {
        const theme = defaultMenuThemes.find((it) => it.name == menuTheme);
        const logoDark = theme?.logoColor == 'dark';
        const menuitemHoverBg = menuTheme === 'white' ? '#e9ecef' : 'rgba(255, 255, 255, 0.1)';
        const menuitemActiveBg = menuTheme === 'white' ? '#ffffff' : 'rgba(255, 255, 255, 0.1)';
        const textColor = menuTheme === 'white' ? '#495057' : menuTheme === 'darkgray' ? 'var(--p-gray-50)' : `var(--p-${menuTheme}-${logoDark ? '950' : '50'})`;

        updatePreset({
            semantic: {
                colorScheme: {
                    light: {
                        sidebar: {
                            bgColor: theme?.color,
                            menuSeparatorBorder: `1px solid ${logoDark ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.2)'}`,
                            menuitemHoverBg,
                            menuitemActiveBg,
                            menuitemTextColor: textColor,
                            menuitemTextActiveColor: `${logoDark ? 'rgba(0, 0, 0, 0.9)' : '#ffffff'}`,
                        },
                    },
                },
            },
        });
    }

    toggleDarkMode(theme: string): void {
        const darkMode = theme === 'dark';

        document.documentElement.classList.toggle('theme-dark', darkMode);
        document.documentElement.classList.toggle('e-dark-mode', darkMode);
    }

    setScale(scale: number) {
        document.documentElement.style.fontSize = `${scale}px`;
    }
}
